/**
 * Ukrainian pluralization rules
 *
 * Ukrainian has 3 plural forms:
 * 1. Nominative singular (1, 21, 31, 41, ..., 101, 121, ...)
 * 2. Genitive singular (2-4, 22-24, 32-34, ..., 102-104, ...)
 * 3. Genitive plural (0, 5-20, 25-30, ..., 100, 105-120, ...)
 *
 * Examples:
 * - 1 питання (one)
 * - 2 питання (few)
 * - 5 питань (many)
 * - 21 питання (one)
 * - 22 питання (few)
 * - 25 питань (many)
 */

export function getUkrainianPluralForm(count: number): 'one' | 'few' | 'many' {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;

  // Special case for 11-14 (they use "many" form)
  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
    return 'many';
  }

  // Last digit is 1: "one" form (1, 21, 31, 41, ...)
  if (lastDigit === 1) {
    return 'one';
  }

  // Last digit is 2, 3, or 4: "few" form (2-4, 22-24, 32-34, ...)
  if (lastDigit >= 2 && lastDigit <= 4) {
    return 'few';
  }

  // All other cases: "many" form (0, 5-20, 25-30, ...)
  return 'many';
}

/**
 * Get the correct Ukrainian plural form for a word
 *
 * @param count - The number to pluralize
 * @param forms - Object with plural forms: { one: string, few: string, many: string }
 * @returns The correct plural form
 *
 * @example
 * pluralizeUkrainian(1, { one: 'питання', few: 'питання', many: 'питань' }) // "питання"
 * pluralizeUkrainian(2, { one: 'питання', few: 'питання', many: 'питань' }) // "питання"
 * pluralizeUkrainian(5, { one: 'питання', few: 'питання', many: 'питань' }) // "питань"
 * pluralizeUkrainian(21, { one: 'питання', few: 'питання', many: 'питань' }) // "питання"
 */
export function pluralizeUkrainian(
  count: number,
  forms: { one: string; few: string; many: string }
): string {
  const form = getUkrainianPluralForm(count);
  return forms[form];
}

/**
 * Format a count with the correct Ukrainian plural form
 *
 * @param count - The number to format
 * @param forms - Object with plural forms: { one: string, few: string, many: string }
 * @returns Formatted string like "5 питань"
 *
 * @example
 * formatCountUkrainian(1, { one: 'питання', few: 'питання', many: 'питань' }) // "1 питання"
 * formatCountUkrainian(5, { one: 'питання', few: 'питання', many: 'питань' }) // "5 питань"
 */
export function formatCountUkrainian(
  count: number,
  forms: { one: string; few: string; many: string }
): string {
  return `${count} ${pluralizeUkrainian(count, forms)}`;
}

/**
 * Alias for pluralizeUkrainian for simpler imports
 * Usage: plural(count, one, few, many)
 */
export function plural(count: number, one: string, few: string, many: string): string {
  return pluralizeUkrainian(count, { one, few, many });
}
